import { BrandManifest, DEFAULT_MANIFEST } from './manifest.schema';
import { resolveVendorManifestPath } from './publicAsset';
import { sanitizeTenantId } from './tenantId';

export type LoadManifestFallback = {
  requestedTenantId: string;
  fallbackTenantId: string;
  reason: string;
};

export type LoadManifestOptions = {
  timeoutMs?: number;
  onFallback?: (details: LoadManifestFallback) => void;
  /** Override manifest fetch base URL (e.g. CDN). Fetches ${manifestBaseUrl}/${tenantId}.json. Must be https (or http for local dev). */
  manifestBaseUrl?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}

function isSafePublicAssetPath(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    !value.includes('..') &&
    !value.includes('://') &&
    !value.startsWith('//')
  );
}

function isOptionalString(value: unknown): value is string | undefined {
  return typeof value === 'undefined' || typeof value === 'string';
}

function isValidManifest(value: unknown): value is BrandManifest {
  if (!isRecord(value)) {
    return false;
  }

  if (typeof value.id !== 'string' || typeof value.displayName !== 'string') {
    return false;
  }

  if (!isRecord(value.theme) || !isRecord(value.theme.tokens)) {
    return false;
  }

  if (!isRecord(value.assets) || !isSafePublicAssetPath(value.assets.logo)) {
    return false;
  }

  const assetKeys = ['sidebar', 'favicon', 'appleTouchIcon', 'pwaIcon192', 'pwaIcon512'] as const;
  for (const key of assetKeys) {
    const assetValue = value.assets[key];
    if (typeof assetValue !== 'undefined' && !isSafePublicAssetPath(assetValue)) {
      return false;
    }
  }

  if (typeof value.metadata !== 'undefined') {
    if (!isRecord(value.metadata)) {
      return false;
    }

    if (
      !isOptionalString(value.metadata.title) ||
      !isOptionalString(value.metadata.description) ||
      !isOptionalString(value.metadata.themeColor)
    ) {
      return false;
    }
  }

  return true;
}

const HTTP_HTTPS_RE = /^https?:\/\//i;

const MIN_REMAINING_TIMEOUT_MS = 250;

function getManifestFetchUrl(tenantId: string, options: LoadManifestOptions): string {
  const base =
    options.manifestBaseUrl ??
    (typeof process !== 'undefined' && process.env?.REACT_APP_VENDOR_MANIFEST_BASE_URL);
  if (base && typeof base === 'string' && HTTP_HTTPS_RE.test(base.trim())) {
    const normalized = base.trim().replace(/\/+$/, '');
    return `${normalized}/${tenantId}.json`;
  }
  return resolveVendorManifestPath(tenantId);
}

async function fetchWithTimeout(input: string, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();

  return new Promise<Response>((resolve, reject) => {
    const timer = setTimeout(() => {
      controller.abort();
      reject(new Error(`Manifest load timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    fetch(input, { cache: 'no-store', signal: controller.signal })
      .then(resolve)
      .catch(reject)
      .finally(() => clearTimeout(timer));
  });
}

async function fetchManifest(
  tenantId: string,
  timeoutMs: number,
  options: LoadManifestOptions
): Promise<BrandManifest> {
  const url = getManifestFetchUrl(tenantId, options);
  const response = await fetchWithTimeout(url, timeoutMs);
  if (!response.ok) {
    throw new Error(`Manifest not found for tenant: ${tenantId}`);
  }

  const manifest = await response.json();
  if (!isValidManifest(manifest)) {
    throw new Error(`Manifest invalid for tenant: ${tenantId}`);
  }

  return manifest;
}

async function resolveDefaultManifest(
  timeoutMs: number,
  options: LoadManifestOptions,
  startTimeMs: number
): Promise<BrandManifest> {
  const elapsedMs = Date.now() - startTimeMs;
  const remainingBudgetMs = timeoutMs - elapsedMs;
  if (remainingBudgetMs <= 0) {
    return DEFAULT_MANIFEST;
  }

  const remainingTimeoutMs = Math.max(MIN_REMAINING_TIMEOUT_MS, remainingBudgetMs);

  try {
    return await fetchManifest(DEFAULT_MANIFEST.id, remainingTimeoutMs, options);
  } catch {
    return DEFAULT_MANIFEST;
  }
}

export async function loadManifest(tenantId: string, options: LoadManifestOptions = {}): Promise<BrandManifest> {
  const timeoutMs = options.timeoutMs ?? 5000;
  const safeTenantId = sanitizeTenantId(tenantId);
  const startTimeMs = Date.now();

  if (!safeTenantId) {
    options.onFallback?.({
      requestedTenantId: tenantId,
      fallbackTenantId: DEFAULT_MANIFEST.id,
      reason: `Invalid tenant identifier: ${tenantId}`
    });
    return resolveDefaultManifest(timeoutMs, options, startTimeMs);
  }

  try {
    return await fetchManifest(safeTenantId, timeoutMs, options);
  } catch (error) {
    const reason = error instanceof Error ? error.message : `Manifest load failed for tenant: ${safeTenantId}`;

    if (safeTenantId === DEFAULT_MANIFEST.id) {
      options.onFallback?.({
        requestedTenantId: tenantId,
        fallbackTenantId: DEFAULT_MANIFEST.id,
        reason
      });
      return DEFAULT_MANIFEST;
    }

    options.onFallback?.({
      requestedTenantId: tenantId,
      fallbackTenantId: DEFAULT_MANIFEST.id,
      reason
    });
    return resolveDefaultManifest(timeoutMs, options, startTimeMs);
  }
}
